import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useSubscription } from "../context/SubscriptionContext";
import { buildApiUrl } from '../services/apiHelpers';

import {
  Bell,
  Settings,
  LogOut,
  Clock,
  MapPin,
  CheckCircle2,
  AlertCircle,
  Clock4,
  XCircle,
  Loader2,
  Menu,
} from "lucide-react";

import web_logo from "../assets/images/web_logo.png";
import SignOutConfirmation from "./SignOutConfirmation";

const STYLES = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;1,9..40,400&family=DM+Mono:wght@400;500&display=swap');

/* ═══════════════════════════════════════════════════════════════
   HEADER HEIGHT & web_logo SIZE CONTROL
   ───────────────────────────────────────────────────────────────
   --hdr-h        → total header height (px)
   --web_logo-h       → web_logo image height (px) — always --hdr-h minus
                    2px top gap + 2px bottom gap, so --hdr-h - 4px.
                    You can override --web_logo-h independently if you
                    want the web_logo smaller than the full bleed height.

   BREAKPOINT TIERS  (change only the values, keep the structure)
   ────────────────────────────────────────────────────────────────
   Desktop      ≥ 1400px  →  --hdr-h: 112px  web_logo height controlled by --web_logo-h
   Large Laptop 1200–1399px →  --hdr-h: 104px  web_logo height controlled by --web_logo-h
   Laptop        992–1199px →  --hdr-h: 96px   web_logo height controlled by --web_logo-h
   Tablet        768–991px  →  --hdr-h: 92px   web_logo height controlled by --web_logo-h
   Mobile        400–767px  →  --hdr-h: 76px   web_logo height controlled by --web_logo-h
   XSmall       < 400px   →  --hdr-h: 64px   web_logo height controlled by --web_logo-h
══════════════════════════════════════════════════════════ */

:root {
  /* ── DESKTOP default (≥ 1400px) ── */
  --hdr-h:   80px;   /* ← change this to resize header on desktop */
  /* adjust desktop web_logo height here */
  --web_logo-h:  calc(var(--hdr-h) - 8px);   /* ← slightly smaller web_logo height on desktop */
  /* adjust desktop web_logo max width here */
  --web_logo-w: 210px;   /* ← slightly smaller web_logo width cap on desktop */

  /* ── Glass / surface ── ULTRA PREMIUM DARK BLUE HEADER */
  --glass-bg:      linear-gradient(135deg, #0f1d42 0%, #060b17 100%);
  --glass-border:  rgba(14, 165, 233, 0.08);
  --glass-shadow:  0 2px 8px rgba(0,0,0,0.4), 0 8px 32px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.05);

  /* ── Premium cyan & sky palette ── */
  --teal:       #00d9ff;
  --teal-dark:  #06b6d4;
  --teal-light: #cffafe;
  --teal-xlight:#ecf9ff;

  /* ── Text colors for blue header ── */
  --ink:    #ffffff;
  --ink-60: #e0e7ff;
  --ink-40: #c7d2fe;
  --ink-20: #a5b4fc;

  /* ── Status pill gradients ── */
  --pill-active-bg:      linear-gradient(135deg, #dbeafe 0%, #c7d2fe 100%);
  --pill-active-text:    #0d47a1;
  --pill-active-border:  rgba(13, 148, 136, 0.4);

  --pill-trial-bg:       linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
  --pill-trial-text:     #0d47a1;
  --pill-trial-border:   rgba(13, 148, 136, 0.4);

  --pill-pending-bg:     linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%);
  --pill-pending-text:   #9a3412;
  --pill-pending-border: rgba(251, 146, 60, 0.4);

  --pill-expired-bg:     linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
  --pill-expired-text:   #991b1b;
  --pill-expired-border: rgba(252, 165, 165, 0.4);

  --pill-loading-bg:     linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  --pill-loading-text:   #475569;
  --pill-loading-border: rgba(148, 163, 184, 0.4);
}

/* ─────────────────────────────────────────────────────────────
   LARGE LAPTOP  1200px – 1399px
   Change --hdr-h here to resize header height on large laptops.
   --web_logo-h is auto-calculated as --hdr-h - 4 (2px top + 2px bottom gap).
───────────────────────────────────────────────────────────── */
@media (max-width: 1399px) {
  :root {
    --hdr-h: 72px;   /* ← large laptop header height */
    /* adjust large laptop web_logo height here */
    --web_logo-h: calc(var(--hdr-h) - 8px);   /* ← slightly smaller large laptop web_logo height */
    /* adjust large laptop web_logo max width here */
    --web_logo-w: 190px;  /* ← slightly smaller large laptop web_logo max width */
  }
}

/* ─────────────────────────────────────────────────────────────
   LAPTOP  992px – 1199px
───────────────────────────────────────────────────────────── */
@media (max-width: 1199px) {
  :root {
    --hdr-h: 68px;    /* ← laptop header height */
    /* adjust laptop web_logo height here */
    --web_logo-h: calc(var(--hdr-h) - 8px);   /* ← slightly smaller laptop web_logo height */
    /* adjust laptop web_logo max width here */
    --web_logo-w: 180px;  /* ← slightly smaller laptop web_logo max width */
  }
}

/* ─────────────────────────────────────────────────────────────
   TABLET  768px – 991px
───────────────────────────────────────────────────────────── */
@media (max-width: 991px) {
  :root {
    --hdr-h: 110px;   /* ← tablet/iPad header height increased by 30% */
    /* adjust tablet/iPad web_logo height here */
    --web_logo-h: calc(var(--hdr-h) - 8px);   /* ← slightly smaller tablet/iPad web_logo height */
    /* adjust tablet/iPad web_logo max width here */
    --web_logo-w: 403px;  /* ← tablet/iPad web_logo width increased by ~40% total */
  }
}

/* ─────────────────────────────────────────────────────────────
   MOBILE  < 768px
   Change only these values to resize the phone header and web_logo.
───────────────────────────────────────────────────────────── */
@media (max-width: 767px) {
  :root {
    --hdr-h: 84px;    /* ← mobile header height increased by about 15% */
    /* adjust mobile web_logo height here */
    --web_logo-h: calc(var(--hdr-h) - 8px);   /* ← slightly smaller mobile web_logo height */
    /* adjust mobile web_logo max width here */
    --web_logo-w: auto;  /* ← mobile web_logo max width */
  }
}

/* ─────────────────────────────────────────────────────────────
   XSMALL  < 400px
───────────────────────────────────────────────────────────── */
@media (max-width: 399px) {
  :root {
    --hdr-h: 60px;   /* ← xsmall header height */
    --web_logo-h: calc(var(--hdr-h) - 8px);  /* ← slightly smaller xsmall web_logo height */
    --web_logo-w: 260px; /* ← slightly smaller xsmall web_logo max width */
  }
}

/* ═══════════════════════════════════════════════════════════════
   HEADER SHELL
══════════════════════════════════════════════════════════════════ */
.cq-header * {
  box-sizing: border-box;
  font-family: 'DM Sans', sans-serif;
}

.cq-header {
  position: sticky;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 190;

  height: var(--hdr-h);         /* controlled by breakpoint vars above */

  background:        var(--glass-bg);
  backdrop-filter:   blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  border-bottom:     1px solid var(--glass-border);
  box-shadow:        var(--glass-shadow);

  display:     flex;
  align-items: center;
  padding:     0;
  overflow:    visible;
}

/* ═══════════════════════════════════════════════════════════════
   THREE-ZONE LAYOUT  [LEFT | CENTER | RIGHT]
══════════════════════════════════════════════════════════════════ */
.hdr-zone {
  display:     flex;
  align-items: center;
}

/* LEFT: hamburger menu + clinic name/code */
.hdr-zone-left {
  flex:           1;
  justify-content:flex-start;
  padding-left:   24px;         /* ← left edge padding on desktop */
  gap:            12px;
  min-width:      0;
}

/* CENTER: web_logo + divider + status pill */
.hdr-zone-center {
  flex:            0 0 auto;
  justify-content: center;
  padding:         0 32px;      /* ← breathing space around web_logo+pill */
  gap:             18px;
}

/* RIGHT: clock + action buttons */
.hdr-zone-right {
  flex:            1;
  justify-content: flex-end;
  padding-right:   24px;        /* ← right edge padding on desktop */
  gap:             10px;
}


/* ═══════════════════════════════════════════════════════════════
   VERTICAL DIVIDER  (thin separator line between elements)
══════════════════════════════════════════════════════════════════ */
.hdr-divider-v {
  width:      1px;
  height:     28px;             /* ← divider height; increase for taller headers */
  background: linear-gradient(180deg, transparent 0%, rgba(0, 217, 255, 0.15) 50%, transparent 100%);
  flex-shrink:0;
  box-shadow: 0 0 8px rgba(0, 217, 255, 0.08);
}


/* ═══════════════════════════════════════════════════════════════
   LEFT ZONE — Clinic name + code
══════════════════════════════════════════════════════════════════ */
.hdr-clinic-block {
  display:        flex;
  flex-direction: column;
  justify-content:center;
  gap: 2px;
}

.hdr-clinic-name {
  font-size:   14px;            /* ← clinic name font size */
  font-weight: 700;
  color:       #ffffff;
  letter-spacing: -0.3px;
  line-height: 1.2;
  white-space: nowrap;
  overflow:    hidden;
  text-overflow: ellipsis;
  max-width:   200px;           /* ← max width before truncation */
  text-shadow: 0 2px 4px rgba(0,0,0,0.2);
}

.hdr-clinic-meta {
  display:     flex;
  align-items: center;
  gap:         4px;
  font-size:   11px;            /* ← clinic code font size */
  font-weight: 500;
  color:       rgba(207, 250, 254, 0.85);
  font-family: 'DM Mono', monospace;
  letter-spacing: 0.3px;
  text-shadow: 0 1px 2px rgba(0,0,0,0.3);
}

.hdr-clinic-meta svg { color: #00d9ff; flex-shrink: 0; filter: drop-shadow(0 1px 2px rgba(0,0,0,0.3)); }

.hdr-clinic-status {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}
.hdr-clinic-status.pending_verification {
  background: rgba(245, 158, 11, 0.14);
  color: #b45309;
}
.hdr-clinic-status.verified {
  background: rgba(16, 185, 129, 0.14);
  color: #047857;
}
.hdr-clinic-status.rejected {
  background: rgba(239, 68, 68, 0.14);
  color: #991b1b;
}


/* ═══════════════════════════════════════════════════════════════
   CENTER ZONE — web_logo
   web_logo height = --web_logo-h (set per breakpoint at top of file).
   The 2px top/bottom gap is baked in: --web_logo-h = --hdr-h - 4px.
══════════════════════════════════════════════════════════════════ */
.hdr-web_logo-wrap {
  height:          var(--web_logo-h);   /* ← auto-tracks breakpoint var */
  display:         flex;
  align-items:     center;
  justify-content: center;
  flex-shrink:     0;
}

.hdr-web_logo-wrap img {
  height:       100%;
  width:        auto;
  max-width:    var(--web_logo-w);  /* ← web_logo width cap controlled by --web_logo-w */
  object-fit:   contain;
  display:      block;
}

/* Mobile web_logo guidance: adjust --web_logo-h and --web_logo-w above inside the mobile @media block. */


/* ═══════════════════════════════════════════════════════════════
   CENTER ZONE — Subscription status pill
══════════════════════════════════════════════════════════════════ */
.hdr-status-pill {
  height:        34px;          /* ← pill height; increase if header is tall */
  padding:       0 14px;
  border-radius: 100px;
  display:       flex;
  align-items:   center;
  gap:           7px;
  font-size:     12.5px;        /* ← pill label font size */
  font-weight:   600;
  letter-spacing:0.1px;
  border:        1px solid;
  white-space:   nowrap;
  transition:    transform 0.2s ease, box-shadow 0.2s ease;
  cursor:        default;
  user-select:   none;
}

.hdr-status-pill:hover {
  transform:  translateY(-2px);
  box-shadow: 0 6px 20px rgba(0,217,255,0.2), inset 0 1px 0 rgba(255,255,255,0.08);
}

/* Status colour variants — swap gradient values to retheme */
.hdr-status-pill.active  { background: var(--pill-active-bg);  color: var(--pill-active-text);  border-color: var(--pill-active-border); }
.hdr-status-pill.trial   { background: var(--pill-trial-bg);   color: var(--pill-trial-text);   border-color: var(--pill-trial-border); }
.hdr-status-pill.pending { background: var(--pill-pending-bg); color: var(--pill-pending-text); border-color: var(--pill-pending-border); }
.hdr-status-pill.expired { background: var(--pill-expired-bg); color: var(--pill-expired-text); border-color: var(--pill-expired-border); }
.hdr-status-pill.loading { background: var(--pill-loading-bg); color: var(--pill-loading-text); border-color: var(--pill-loading-border); }


/* ═══════════════════════════════════════════════════════════════
   RIGHT ZONE — Live clock widget
══════════════════════════════════════════════════════════════════ */
.hdr-clock {
  display:        flex;
  flex-direction: column;
  justify-content:center;
  align-items:    flex-end;
  padding:        0 14px;
  height:         46px;         /* ← clock widget height */
  background:     linear-gradient(135deg, rgba(0, 217, 255, 0.12) 0%, rgba(6, 182, 212, 0.08) 100%);
  border:         1px solid rgba(0, 217, 255, 0.25);
  border-radius:  14px;
  gap:            2px;
  flex-shrink:    0;
  backdrop-filter: blur(10px);
  box-shadow:     0 2px 8px rgba(0, 217, 255, 0.1), inset 0 1px 0 rgba(255,255,255,0.08);
}

.hdr-time {
  display:        flex;
  align-items:    center;
  gap:            5px;
  font-size:      13px;         /* ← time font size */
  font-weight:    700;
  color:          #ffffff;
  font-family:    'DM Mono', monospace;
  letter-spacing: 0.5px;
  line-height:    1;
  text-shadow:    0 1px 2px rgba(0,0,0,0.3);
}

.hdr-time svg { color: #00d9ff; filter: drop-shadow(0 1px 2px rgba(0,0,0,0.2)); }

.hdr-date {
  font-size:      10px;         /* ← date label font size */
  font-weight:    500;
  color:          rgba(207, 250, 254, 0.7);
  letter-spacing: 0.3px;
  line-height:    1;
  text-align:     right;
  text-shadow:    0 1px 2px rgba(0,0,0,0.2);
}


/* ═══════════════════════════════════════════════════════════════
   RIGHT ZONE — Icon action buttons  (bell / settings / web_logout)
══════════════════════════════════════════════════════════════════ */
.hdr-icon-btn {
  position:      relative;
  width:         46px;          /* ← button size on desktop */
  height:        46px;          /* ← button size on desktop */
  border-radius: 16px;
  border:        1px solid rgba(0, 217, 255, 0.15);
  background:    linear-gradient(135deg, rgba(0, 217, 255, 0.08) 0%, rgba(6, 182, 212, 0.05) 100%);
  display:       flex;
  align-items:   center;
  justify-content:center;
  cursor:        pointer;
  color:         #ffffff;
  transition:    all 0.25s ease;
  flex-shrink:   0;
  backdrop-filter: blur(8px);
  box-shadow:    0 2px 6px rgba(0,0,0,0.15);
}

.hdr-icon-btn:hover {
  background:  linear-gradient(135deg, rgba(0, 217, 255, 0.15) 0%, rgba(6, 182, 212, 0.12) 100%);
  border-color:rgba(0, 217, 255, 0.35);
  color:       #00d9ff;
  transform:   translateY(-2px);
  box-shadow:  0 6px 16px rgba(0, 217, 255, 0.15), inset 0 1px 0 rgba(255,255,255,0.1);
}

/* Danger variant — web_logout button */
.hdr-icon-btn.danger {
  background: #dc2626;
  border-color: #b91c1c;
  color: #ffffff;
}

.hdr-icon-btn.danger:hover {
  background: #991b1b;
  border-color: #7f1d1d;
  color: #ffffff;
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(220, 38, 38, 0.25), inset 0 1px 0 rgba(255,255,255,0.12);
}

/* Notification red dot on bell button */
.hdr-notif-dot {
  position:     absolute;
  top:          9px;
  right:        9px;
  width:        7px;            /* ← dot size */
  height:       7px;
  border-radius:50%;
  background:   #ef4444;
  border:       1.5px solid white;
  box-shadow:   0 0 0 2px rgba(239,68,68,0.2);
}


/* ═══════════════════════════════════════════════════════════════
   ANIMATION
══════════════════════════════════════════════════════════════════ */
.spin-icon { animation: spin 1.2s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }


/* ═══════════════════════════════════════════════════════════════
   RESPONSIVE — LAPTOP  (992px – 1199px)
   Heights already set via :root override above.
   Layout: [status pill] | [bell + settings + web_logout]
   Hidden at this size: clinic block, clock, hamburger, web_logo
══════════════════════════════════════════════════════════════════ */
@media (max-width: 1199px) {

  /* Hide clinic name + code and its preceding divider */
  .hdr-zone-left .hdr-divider-v,
  .hdr-zone-left .hdr-clinic-block { display: none; }

  /* Hide clock and the divider before the icon buttons */
  .hdr-zone-right .hdr-clock,
  .hdr-zone-right .hdr-divider-v  { display: none; }

  .hdr-zone-left   { padding-left:  20px; }  /* ← left padding on laptop */
  .hdr-zone-right  { padding-right: 20px; }  /* ← right padding on laptop */
  .hdr-zone-center { padding: 0 20px; }       /* ← center padding on laptop */

  /* Slightly smaller buttons on laptop */
  .hdr-icon-btn { width: 44px; height: 44px; }
}

/* ─────────────────────────────────────────────────────────────
   DESKTOP  (>= 1400px)
   Hide the hamburger menu and web_logo only on desktop screens.
   Keep the menu visible on tablets and smaller widths.
───────────────────────────────────────────────────────────── */
@media (min-width: 1400px) {
  .hdr-menu-btn,
  .hdr-web_logo-wrap,
  .hdr-zone-center .hdr-divider-v {
    display: none;
  }
}


/* ═══════════════════════════════════════════════════════════════
   RESPONSIVE — TABLET  (768px – 991px)
   Layout: hamburger | [web_logo + pill] | [bell + settings + web_logout]
   Pill is still visible on tablet but shrinks slightly
══════════════════════════════════════════════════════════════════ */
@media (max-width: 991px) {

  .hdr-zone-left   { padding-left:  16px; }  /* ← left padding on tablet */
  .hdr-zone-right  { padding-right: 16px; gap: 8px; }
  .hdr-zone-center { padding: 0 12px; gap: 14px; }

  /* Shrink pill on tablet */
  .hdr-status-pill {
    height:    30px;            /* ← pill height on tablet */
    font-size: 11.5px;          /* ← pill font on tablet */
    padding:   0 11px;
  }

  .hdr-icon-btn { width: 42px; height: 42px; border-radius: 14px; }
}


/* ═══════════════════════════════════════════════════════════════
   RESPONSIVE — MOBILE  (< 768px)
   Layout: hamburger | [web_logo] | [bell]
   Status pill is hidden to save space; show it again by
   removing the display:none below if you prefer to keep it.
══════════════════════════════════════════════════════════════════ */
@media (max-width: 767px) {

  .hdr-zone-left  { padding-left:  12px; }   /* ← left padding on mobile */
  .hdr-zone-right { padding-right: 12px; gap: 8px; }
  .hdr-zone-center{ padding: 0 6px; gap: 0; }

  /* Hide status pill on mobile to keep header clean */
  .hdr-status-pill          { display: none; }  /* ← remove line to keep pill */
  .hdr-zone-center .hdr-divider-v { display: none; }

  /* On mobile hide settings + web_logout; keep only bell */
  .hdr-icon-btn:not(.hdr-bell-btn):not(.hdr-menu-btn) { display: none; }
  /* ↑ To show all buttons on mobile, delete the line above */

  .hdr-icon-btn { width: 40px; height: 40px; border-radius: 12px; }
}


/* ═══════════════════════════════════════════════════════════════
   RESPONSIVE — XSMALL  (< 400px)
   Layout: hamburger | web_logo | (nothing)
══════════════════════════════════════════════════════════════════ */
@media (max-width: 399px) {

  .hdr-zone-left  { padding-left:  10px; }
  .hdr-zone-right { padding-right: 10px; gap: 6px; }

  /* Optional pixel cap on tiny screens so web_logo doesn't overflow */
  .hdr-web_logo-wrap img { max-width: 130px; }  /* ← cap web_logo width on xsmall */

  .hdr-icon-btn { width: 36px; height: 36px; border-radius: 10px; }
}
`;

/* ═══════════════════════════════════════════════════════════════
   STATUS CONFIG
   Add new statuses here by following the same shape.
   label(d) receives the trial days-remaining count.
══════════════════════════════════════════════════════════════════ */
const STATUS_CONFIG = {
  TRIAL: {
    label: (d) => `Trial · ${d}d left`,
    cls:   "trial",
    Icon:  Clock4,
  },
  SUBSCRIBED: {
    label: () => "Active",
    cls:   "active",
    Icon:  CheckCircle2,
  },
  PENDING_APPROVAL: {
    label: () => "Pending Approval",
    cls:   "pending",
    Icon:  AlertCircle,
  },
  EXPIRED: {
    label: () => "Expired",
    cls:   "expired",
    Icon:  XCircle,
  },
  LOADING: {
    label: () => "Checking…",
    cls:   "loading",
    Icon:  Loader2,
    spin:  true,
  },
};

export default function Header({ onMenuClick }) {
  const navigate = useNavigate();
  const [showSignOutModal, setShowSignOutModal] = useState(false);
  const [now, setNow]             = useState(new Date());
  const [clinicData, setClinicData] = useState({ name: "Loading...", code: "" });

  const { auth, refresh } = useAuth();
  const { subscriptionData, loading } = useSubscription();
  const clinicCode = auth?.clinic?.code || null;
  const clinicName = auth?.clinic?.name || null;

  const handleSignOut = async () => {
    try {
      await fetch(buildApiUrl("clinic/auth/logout"), {
        method: "POST",
        credentials: "include",
      });
    } catch (error) {
      console.warn("Sign-out request failed", error);
    } finally {
      try {
        const keys = ['clinicId','clinicName','clinicCode','doctorId','subscriptionStatus','isActive','trialEndDate','debug'];
        keys.forEach((key) => localStorage.removeItem(key));
        sessionStorage.removeItem("clinique.initial-loader-completed");
      } catch (error) {
        console.warn('Failed to clear legacy localStorage keys', error);
      }
      await refresh();
      setShowSignOutModal(false);
      navigate("/");
    }
  };

  /* Live clock — ticks every second */
  useEffect(() => {
    const iv = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(iv);
  }, []);

  /* Time formatter — IST, 12-hour with seconds */
  const fmtTime = (d) =>
    d.toLocaleTimeString("en-IN", {
      hour:     "2-digit",
      minute:   "2-digit",
      second:   "2-digit",
      hour12:   true,
      timeZone: "Asia/Kolkata",
    });

  /* Date formatter — e.g. "Sun, 07 Jun" */
  const fmtDate = (d) =>
    d.toLocaleDateString("en-IN", {
      weekday:  "short",
      day:      "2-digit",
      month:    "short",
      timeZone: "Asia/Kolkata",
    });

  /* Fetch clinic display name + code */
  useEffect(() => {
    const fetchClinic = async () => {
      if (!clinicCode) {
        setClinicData({ name: clinicName || "Clinic", code: "---", verificationStatus: "UNKNOWN" });
        return;
      }

      try {
        const res = await fetch(buildApiUrl(`clinic/${clinicCode}`));
        const d   = await res.json();
        setClinicData({
          name: d.clinicName || d.name || d.displayName || d.clinic_name || clinicName || "Clinic",
          code: d.clinicCode || d.code || clinicCode || "---",
          verificationStatus: d.verificationStatus || d.status || "UNKNOWN",
        });
      } catch {
        setClinicData({
          name: clinicName || "Clinic",
          code: clinicCode || "---",
          verificationStatus: "UNKNOWN",
        });
      }
    };
    fetchClinic();
  }, [clinicCode, clinicName]);

  /* Normalise API mode string → STATUS_CONFIG key */
  const rawMode       = loading ? "LOADING" : subscriptionData?.mode || "EXPIRED";
  const normalizedMode =
    rawMode === "ACTIVE"   ? "SUBSCRIBED"       :
    rawMode === "PENDING"  ? "PENDING_APPROVAL" :
    rawMode;

  const cfg        = STATUS_CONFIG[normalizedMode] || STATUS_CONFIG.EXPIRED;
  const StatusIcon = cfg.Icon;
  const isExpired = normalizedMode === "EXPIRED";

  const trialDaysLeft = () => {
    if (!subscriptionData?.trialEndDate) return 0;
    const days = Math.ceil((new Date(subscriptionData.trialEndDate) - new Date()) / 86400000);
    return days > 0 ? days : 0;
  };

  return (
    <>
      <style>{STYLES}</style>

      <header className="cq-header">

        {/* ── LEFT ZONE ────────────────────────────────────────────────
            Desktop / Laptop: hamburger | divider | clinic name + code
            Tablet / Mobile:  hamburger only (clinic block hidden via CSS)
        ─────────────────────────────────────────────────────────────── */}
        <div className="hdr-zone hdr-zone-left">

          {/* Hamburger — always visible, calls onMenuClick to open sidebar */}
          <button
            className="hdr-icon-btn hdr-menu-btn"
            onClick={onMenuClick}
            type="button"
            aria-label="Open navigation menu"
          >
            <Menu size={18} />
          </button>

          {/* Thin vertical separator — hidden on tablet/mobile via CSS */}
          <div className="hdr-divider-v" />

          {/* Clinic identity block — hidden on tablet/mobile via CSS */}
          <div className="hdr-clinic-block">
            <div className="hdr-clinic-name">{clinicData.name}</div>
            <div className="hdr-clinic-meta">
              <MapPin size={10} />
              {clinicData.code}
              {typeof clinicData.verificationStatus === "string" && clinicData.verificationStatus !== "UNKNOWN" && (
                <div className={`hdr-clinic-status ${clinicData.verificationStatus.toLowerCase().replace(/_/g, "_")}`}>
                  {clinicData.verificationStatus === "PENDING_VERIFICATION"
                    ? "Pending verification"
                    : clinicData.verificationStatus === "VERIFIED"
                      ? "Verified"
                      : clinicData.verificationStatus === "REJECTED"
                        ? "Rejected"
                        : clinicData.verificationStatus}
                </div>
              )}
            </div>
          </div>

        </div>

        {/* ── CENTER ZONE ──────────────────────────────────────────────
            web_logo (always visible) + divider + status pill
            Pill hidden on mobile via CSS; divider hidden too.
        ─────────────────────────────────────────────────────────────── */}
        <div className="hdr-zone hdr-zone-center">

          {/* web_logo — height tracks --web_logo-h CSS variable */}
          <div className="hdr-web_logo-wrap">
            <img src={web_logo} alt="CliniQueue" />
          </div>

          {/* Divider between web_logo and status pill — hidden on mobile */}
          <div className="hdr-divider-v" style={{ margin: "0 18px" }} />

        </div>

        {/* ── RIGHT ZONE ───────────────────────────────────────────────
            Desktop: clock | divider | status pill | bell | settings | web_logout
            Laptop:  bell | settings | web_logout  (clock hidden)
            Tablet:  bell | settings | web_logout  (clock hidden)
            Mobile:  bell only  (settings + web_logout hidden via CSS)
        ─────────────────────────────────────────────────────────────── */}
        <div className="hdr-zone hdr-zone-right">

          {/* Live clock widget — hidden below 1200px via CSS */}
          <div className="hdr-clock">
            <div className="hdr-time">
              <Clock size={11} />
              {fmtTime(now)}
            </div>
            <div className="hdr-date">{fmtDate(now)}</div>
          </div>

          {/* Separator before status pill */}
          <div className="hdr-divider-v" style={{ margin: "0 2px" }} />

          {/* Subscription status pill */}
          <div className={`hdr-status-pill ${cfg.cls}`}>
            <StatusIcon
              size={13}
              strokeWidth={2.2}
              className={cfg.spin ? "spin-icon" : ""}
            />
            {cfg.label(normalizedMode === 'TRIAL' ? trialDaysLeft() : undefined)}
          </div>

          {/* Separator before buttons — hidden below 1200px via CSS */}
          <div className="hdr-divider-v" style={{ margin: "0 2px" }} />

          {/* Bell / Notifications */}
          <button className="hdr-icon-btn hdr-bell-btn" aria-label="Notifications">
            <Bell size={17} />
            <span className="hdr-notif-dot" />
          </button>

          {/* Settings — navigates to settings page */}
          <button
            className={`hdr-icon-btn${isExpired ? " disabled" : ""}`}
            onClick={() => !isExpired && navigate("/admin-dashboard/settings")}
            disabled={isExpired}
            aria-label="Settings"
            aria-disabled={isExpired}
            title={isExpired ? "Settings is unavailable while your subscription is expired" : "Settings"}
          >
            <Settings size={17} />
          </button>

          {/* web_logout — opens confirmation modal */}
          <button
            className="hdr-icon-btn danger"
            onClick={() => setShowSignOutModal(true)}
            aria-label="Sign out"
          >
            <LogOut size={17} />
          </button>

        </div>

      </header>

      {/* Sign-out confirmation dialog */}
        <SignOutConfirmation
        open={showSignOutModal}
        onCancel={() => setShowSignOutModal(false)}
        onConfirm={handleSignOut}
      />
    </>
  );
}
